/**
 * TiersTab — 4 capability-tier cards (tier_1_legacy, tier_2_plus,
 * tier_3_integrated, tier_internal) plus the member override lookup panel.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Chip, Button, Alert, Skeleton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EmptyState from '../../common/EmptyState';
import MetricCard from '../../common/MetricCard';
import StatusBadge from '../../common/StatusBadge';
import TierEditDialog from './TierEditDialog';
import MemberOverridePanel from './MemberOverridePanel';
import { colors } from '../../../theme/designTokens';
import { quillApi, QuillApiError, CapabilityTier, TierPatch } from '../../../services/quillAdminService';
import { categoriesToArray } from './guardrailUi';

const TiersTab: React.FC = () => {
  const [tiers, setTiers] = useState<CapabilityTier[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTier, setEditingTier] = useState<CapabilityTier | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await quillApi.guardrails.getTiers();
      setTiers(data);
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Failed to load capability tiers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (patch: TierPatch) => {
    if (!editingTier) return;
    setSaving(true);
    setSaveError(null);
    try {
      await quillApi.guardrails.updateTier(editingTier.tier_id, patch);
      setEditingTier(null);
      await load();
    } catch (e) {
      setSaveError(e instanceof QuillApiError ? e.message : 'Failed to save tier');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={load}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!tiers || tiers.length === 0) {
    return <EmptyState icon="no_data" title="No capability tiers found" description="Expected 4 seeded tiers — check the backend." action={{ label: 'Retry', onClick: load }} />;
  }

  // Suggestions for the editor: every category any tier already carries, minus
  // the "*" wildcard, which is a whole-list statement rather than a name.
  const knownCategories = Array.from(
    new Set(tiers.flatMap((t) => categoriesToArray(t.capability_categories)))
  ).filter((c) => c !== '*').sort();

  return (
    <Box>
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Grid container spacing={2}>
        {tiers.map((tier) => {
          const categories = categoriesToArray(tier.capability_categories);
          return (
            <Grid item xs={12} sm={6} md={3} key={tier.tier_id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: `4px solid ${colors.deepTeal[500]}`,
                }}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {tier.tier_name}
                    </Typography>
                    <StatusBadge status={tier.is_active ? 'active' : 'inactive'} size="sm" />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', display: 'block', mb: 1 }}>
                    {tier.tier_id}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, minHeight: 40 }}>
                    {tier.tier_description || 'No description'}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <MetricCard title="Members" value={tier.member_count} icon="users" color="users" variant="compact" />
                  </Box>

                  {/* ── A-F1: the capability list is READ again. quill-engine's
                      llmClassifier appends it to the constitutional
                      classifier's system prompt as an ADDITIVE allowance, so
                      this card no longer files it under "not read by the
                      engine" — that label is now as wrong as the old
                      "Logging off" chip was. The classifier model/temperature
                      below stay under that heading, because they really are
                      inert. */}
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', letterSpacing: '0.06em' }}>
                    Allowed for this tier · added to the classifier prompt
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 1, minHeight: 28 }}>
                    {categories.length === 0 ? (
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        No explicit allowances
                      </Typography>
                    ) : (
                      categories.map((c) => <Chip key={c} label={c} size="small" variant="outlined" />)
                    )}
                  </Box>

                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', letterSpacing: '0.06em' }}>
                    Recorded on the row · not read by the engine
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                    Classifier: <strong>{tier.classifier_model || '—'}</strong> · temp{' '}
                    <strong>{tier.classifier_temperature ?? '—'}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 2 }}>
                    The live classifier model comes from the Pipeline Models page.
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={tier.fast_path_enabled ? 'Fast-path on' : 'Fast-path off'}
                      size="small"
                      color={tier.fast_path_enabled ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ mt: 'auto' }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setEditingTier(tier)}
                      sx={{ borderColor: colors.deepTeal[500], color: colors.deepTeal[500] }}
                    >
                      Edit
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <MemberOverridePanel tiers={tiers} />

      {editingTier && (
        // A-F1: the capability-categories editor is back, because quill-engine
        // reads the column again. The suggestion list is the union of every
        // tier's stored categories, so the common ones are one keystroke away
        // and a typo — which the engine would drop the whole list over — is
        // less likely than free-typing every name.
        <TierEditDialog
          tier={editingTier}
          open={!!editingTier}
          saving={saving}
          onClose={() => (saving ? null : setEditingTier(null))}
          onSave={handleSave}
          knownCategories={knownCategories}
        />
      )}
    </Box>
  );
};

export default TiersTab;
